import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import BlogPost from "../components/BlogPost.js"
import SEO from "../components/SEO.js"
import { BASE_ASSETS_URL } from "../utils/constants.js"
import { getLocalBusinessSchema } from "../utils/structuredData.js"
import "../style/ctaSection.css"

const HomePage = ({ text }) => {
    const [isMobile, setIsMobile] = useState(false)
    const videoReference = useRef(null)
    const [isVideoLoaded, setIsVideoLoaded] = useState(false)

    useEffect(() => {
        const video = videoReference.current
        if (video) {
            // Set up video loading state
            video.addEventListener('loadeddata', () => {
                setIsVideoLoaded(true)
            })

            // Try to play the video
            const playPromise = video.play()

            if (playPromise !== undefined) {
                playPromise.catch(error => {
                    console.log("Video autoplay failed:", error)
                })
            }
        }

        return () => {
            if (video) {
                video.removeEventListener('loadeddata', () => { })
            }
        }
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768)
        }

        handleResize()

        // Event listener for window resize
        window.addEventListener('resize', handleResize)

        // Clean up event listener
        return () => {
            window.removeEventListener('resize', handleResize)
        }
    }, [])

    const blogPosts = [{
        key: "home-drockerhof",
        title: text.homeSubtitle,
        subtitle: "",
        body: text.homeBody
    },
    {
        key: "home-drockerhof-2",
        title: text.homeSubtitle2,
        subtitle: "",
        body: text.homeBody2
    }]

    // Structured data for the business
    const structuredData = getLocalBusinessSchema({
        name: text.homeTitle,
        description: text.homeSubtitle,
        address: {
            streetAddress: "San Giacomo 18",
            addressLocality: "Funes",
            addressRegion: "South Tyrol",
            postalCode: "39040",
            addressCountry: "IT"
        },
        geo: {
            latitude: "46.65406102851609",
            longitude: "11.650029741684827"
        },
        telephone: "+390472840030",
        openingHours: "Mo-Su 00:00-24:00",
        priceRange: "€€"
    })

    return (
        <>
            <SEO
                title={text.homeTitle}
                description={text.homeSubtitle}
                structuredData={structuredData}
                alternateUrls={[
                    { lang: 'de', url: 'https://www.drockerhof.it/de' },
                    { lang: 'it', url: 'https://www.drockerhof.it/it' },
                    { lang: 'en', url: 'https://www.drockerhof.it/en' }
                ]}
            />
            <div className="drocker-body">
                <div className={isMobile ? "drocker-video-container" : ""}>
                    {!isVideoLoaded && (
                        <div className="video-placeholder" style={{
                            width: '100%',
                            backgroundColor: '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100vh',
                        }}>
                            <div className="spinner-border text-primary" role="status" style={{
                                width: '5rem',
                                height: '5rem',
                                borderColor: 'black',
                                borderRightColor: 'transparent'
                            }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}
                    <div className="position-relative">
                        <video
                            ref={videoReference}
                            className={`${isMobile ? "" : "w-100"} ${!isVideoLoaded ? "d-none" : ""}`}
                            autoPlay
                            muted
                            loop
                            playsInline
                            preload="metadata"
                            loading="lazy"
                        >
                            <source src={`${BASE_ASSETS_URL}videos/drockerhof_intro.mp4`} type="video/mp4" />
                            <source src={`${BASE_ASSETS_URL}videos/drockerhof_intro.webm`} type="video/webm" />
                        </video>
                        <div className="container drocker-h1-video-container">
                            <h1 className="text-white mb-0 drocker-title-outline">{text.homeTitle}</h1>
                        </div>
                    </div>
                </div>
                <div className="container container-fluid">
                    <div className="container container-fluid pt-4">
                        <BlogPost key={blogPosts[0].key} title={blogPosts[0].title} subtitle={blogPosts[0].subtitle} body={blogPosts[0].body} images={blogPosts[0].images} />
                        <section 
                            className="cta-section py-5 my-5" 
                            aria-labelledby="cta-heading"
                            itemScope 
                            itemType="https://schema.org/Offer"
                        >
                            <div className="row justify-content-center">
                                <div className="col-lg-8 text-center">
                                    <h2 id="cta-heading" className="display-5 mb-4" itemProp="name" >
                                        {text.ctaTitle}
                                    </h2>
                                    <p className="lead mb-4" itemProp="description" >
                                        {text.ctaDescription}
                                    </p>
                                    <div className="d-flex justify-content-center gap-3">
                                        <Link 
                                            to="bookroom" 
                                            className="btn btn-primary btn-lg px-4"
                                            itemProp="url"
                                            aria-label={`${text.ctaButton} - ${text.ctaTitle}`}
                                        >
                                            {text.ctaButton}
                                        </Link>
                                        <Link 
                                            to="apartments" 
                                            className="btn btn-outline-primary btn-lg px-4"
                                            itemProp="url"
                                            aria-label={`${text.ctaSecondaryButton} - ${text.ctaTitle}`}
                                        >
                                            {text.ctaSecondaryButton}
                                        </Link>
                                    </div>
                                    <meta itemProp="availability" content="https://schema.org/InStock" />
                                </div>
                            </div>
                        </section>
                        <BlogPost key={blogPosts[1].key} title={blogPosts[1].title} subtitle={blogPosts[1].subtitle} body={blogPosts[1].body} images={blogPosts[1].images} />
                    </div>
                </div>
            </div>
        </>
    )
}

export default HomePage
